import type {
  RentalStructure,
  RentalStructureOptionConfig,
} from '../types/configuration';

export interface ResolvedRentalStructureOption {
  key: string;
  label: string;
}

/**
 * Normalizes rental_structure_options from either format into a consistent
 * ResolvedRentalStructureOption[] array:
 *
 * - New format (array):  [{ key: 'adu', label: 'ADU' }, ...] — labels come from config as-is
 * - Legacy format (dict): { adu: true, apartment: false, ... } — no per-option labels; key is used
 *
 */
export function resolveRentalStructureOptions(
  options: RentalStructureOptionConfig[] | RentalStructure | undefined | null
): ResolvedRentalStructureOption[] {
  if (!options) return [];

  if (Array.isArray(options)) {
    return options.map(({ key, label }) => ({
      key,
      label: label ?? key,
    }));
  }

  // Legacy object format: { key: boolean } — filter out disabled entries
  return Object.entries(options)
    .filter(([, enabled]) => enabled)
    .map(([key]) => ({
      key,
      label: key,
    }));
}

/**
 * Returns the display label for a given rental structure key.
 * Uses the label from resolved options (from customer config); otherwise the key.
 */
export function getLabelByKey(
  key: string | undefined | null,
  options: ResolvedRentalStructureOption[]
): string | undefined {
  if (!key) return undefined;
  return options.find((opt) => opt.key === key)?.label ?? key;
}
